import { useCallback, useEffect, useMemo, useRef } from 'react';
import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods, QuotesV2Api } from '@setvi/shared/enums';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import type { QuoteV2ItemMetadata } from 'Services/Query/QuotesV2/Types';
import { useQuoteCache } from './use-quote-cache';
import { calculateGrossProfitPercentage } from '../utils/pricing-helpers';

const DEBOUNCE_MS = 250;

/** Recursively collect all item IDs from a nested QuoteItemDTO tree */
const collectAllIds = (items: QuoteItemDTO[]): string[] => {
  const ids: string[] = [];
  for (const item of items) {
    const id = item.id || (item as any)._id;
    if (id) ids.push(id);
    const children = (item as any).children as QuoteItemDTO[] | undefined;
    if (children?.length) ids.push(...collectAllIds(children));
  }
  return ids;
};

/**
 * Flatten a raw V2 API product item into the same shape the cache uses.
 */
const flattenEnrichedProduct = (raw: QuoteV2ItemMetadata) => {
  const p = raw.product || ({} as any);
  const sell = p.sell || 0;
  const cost = p.cost || 0;

  return {
    productId: p.productId || '',
    name: p.name || '',
    sku: p.sku || '',
    quantity: p.quantity || 1,
    sell,
    cost,
    note: p.note || null,
    vendor: p.vendor || null,
    images: p.images || null,
    imageUrl: p.images?.[0]?.path || '',
    isFreeHand: p.isFreeHand || false,
    isEditable: p.isEditable || false,
    labelType: p.labelType || '',
    customLabel: p.customLabel || '',
    includeInTotalCost: p.includeInTotalCost || false,
    minimumQuoteQuantity: p.minimumQuoteQuantity ?? 1,
    overrideMinimumQuoteQuantity: p.overrideMinimumQuoteQuantity ?? false,
    totalGrossProfit: sell - cost,
    totalGrossProfitPercentage: calculateGrossProfitPercentage(cost, sell),
    description: p.description || '',
    packSize: p.packSize || '',
    unitOfMeasure: p.unitOfMeasure || '',
    manufacturerSku: p.manufacturerSku || '',
    position: raw.position || null,
    _enriched: true
  };
};

/**
 * Flatten a raw V2 API section item into the shape the cache uses.
 */
const flattenEnrichedSection = (raw: QuoteV2ItemMetadata) => ({
  name: raw.section?.name || '',
  note: raw.section?.note || null,
  position: raw.position || null,
  _enriched: true
});

/**
 * Recursively merge enriched fields into the nested cache tree (immutable).
 */
const mergeEnrichedItems = (
  cachedItems: QuoteItemDTO[],
  enrichedMap: Map<string, QuoteV2ItemMetadata>
): QuoteItemDTO[] =>
  cachedItems.map(item => {
    const id = item.id || (item as any)._id;
    const enriched = id ? enrichedMap.get(id) : undefined;

    let mergedItem: QuoteItemDTO;
    if (enriched) {
      const flat =
        enriched.type === 'section'
          ? flattenEnrichedSection(enriched)
          : flattenEnrichedProduct(enriched);
      mergedItem = { ...item, ...flat } as QuoteItemDTO;
    } else {
      mergedItem = item;
    }

    const { children } = mergedItem as any;
    if (children?.length) {
      return {
        ...mergedItem,
        children: mergeEnrichedItems(children as QuoteItemDTO[], enrichedMap)
      } as unknown as QuoteItemDTO;
    }
    return mergedItem;
  });

/**
 * Viewport-only item enrichment.
 *
 * Watches visible root items from the outer virtualizer, collects all
 * descendant IDs, and fetches full item data for those not yet enriched.
 * Uses a Set<string> ref as the primary guard against duplicate fetches —
 * this is stable across re-renders and doesn't depend on markers surviving
 * data transformations.
 *
 * The Set is cleared when the overall items array changes structurally
 * (metadata refetch after mutations), detected via a fingerprint.
 */
export const useEnrichVisibleItems = (
  quoteId: string,
  visibleItems: QuoteItemDTO[],
  allItems: QuoteItemDTO[]
) => {
  const cache = useQuoteCache(quoteId);
  const enrichedIdsRef = useRef(new Set<string>());
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFetchingRef = useRef(false);

  // Stable fingerprint of the full items array — changes on metadata refetch
  // or structural mutations (add/remove/reorder), NOT on enrichment merges.
  const allItemsFingerprint = useMemo(
    () =>
      allItems
        .map(i => {
          const id = i.id || i._id;
          const children = (i as any).children || [];
          const gc = children
            .map((c: any) => c.children?.length || 0)
            .join('.');
          return `${id}:${children.length}:${gc}`;
        })
        .join(','),
    [allItems]
  );

  // When items structure changes, rebuild enriched IDs from items
  // that already have _enriched: true (preserves existing enrichment)
  const prevFingerprintRef = useRef(allItemsFingerprint);
  if (prevFingerprintRef.current !== allItemsFingerprint) {
    prevFingerprintRef.current = allItemsFingerprint;
    const stillEnriched = new Set<string>();
    const scanEnriched = (items: QuoteItemDTO[]) => {
      for (const item of items) {
        const id = item.id || (item as any)._id;
        if (id && (item as any)._enriched) stillEnriched.add(id);
        const children = (item as any).children as QuoteItemDTO[] | undefined;
        if (children?.length) scanEnriched(children);
      }
    };
    scanEnriched(allItems);
    enrichedIdsRef.current = stillEnriched;
  }

  // Stable string of visible descendant IDs — only changes when
  // actually different items scroll into view, NOT on every render.
  const visibleIds = useMemo(() => collectAllIds(visibleItems), [visibleItems]);
  const visibleIdsKey = useMemo(() => visibleIds.join(','), [visibleIds]);

  const fetchAndMerge = useCallback(
    async (ids: string[]) => {
      if (!ids.length || !quoteId || isFetchingRef.current) return;

      isFetchingRef.current = true;
      try {
        const raw = (await axiosHelper({
          endpoint: QuotesV2Api.Items.replace(':quoteId', quoteId),
          method: AxiosMethods.POST,
          baseURL: process.env.REVOPS_API,
          body: { ids }
        })) as QuoteV2ItemMetadata[];

        const enrichedMap = new Map<string, QuoteV2ItemMetadata>();
        for (const item of raw) {
          const id = (item as any)._id || (item as any).id;
          if (id) enrichedMap.set(id, item);
        }

        cache.setItems(prev => {
          if (!prev) return prev;
          return mergeEnrichedItems(prev, enrichedMap);
        });

        // Mark all requested IDs as enriched
        for (const id of ids) {
          enrichedIdsRef.current.add(id);
        }
      } finally {
        isFetchingRef.current = false;
      }
    },
    [quoteId, cache]
  );

  // Debounced enrichment — only fires when visible IDs actually change
  useEffect(() => {
    const unenrichedIds = visibleIds.filter(
      id => !enrichedIdsRef.current.has(id)
    );
    if (!unenrichedIds.length) return undefined;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Re-check at execution time (some may have been enriched during debounce)
      const stillNeeded = unenrichedIds.filter(
        id => !enrichedIdsRef.current.has(id)
      );
      if (stillNeeded.length) {
        fetchAndMerge(stillNeeded);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // visibleIdsKey is a stable string — doesn't change on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIdsKey, fetchAndMerge]);
};
